import React, { useState, useEffect } from 'react';
import {
  CheckCircle, XCircle, Loader2, Inbox,
  Search, FileText, Grid3X3, Filter, Eye, Calendar,
  User, Tag, Ruler, Hash, ImageIcon, ChevronDown,
  Clock, AlertCircle, Trash2,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';
import PageThumbnailPreview from '../../components/Editor/PageThumbnailPreview';
import LetterTile from '../../components/shared/LetterTile';
import { getCategoriesForSlug } from '../../constants/templateCategories';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending:   { label: 'Pending',   bg: 'bg-amber-100 text-amber-700',    dot: 'bg-amber-400' },
  approved:  { label: 'Approved',  bg: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  rejected:  { label: 'Rejected',  bg: 'bg-red-100 text-red-700',         dot: 'bg-red-400' },
  published: { label: 'Published', bg: 'bg-blue-100 text-blue-700',       dot: 'bg-blue-500' },
};


const AdminSubmissions = () => {
  const { toast } = useToast();
  const [submissions, setSubmissions]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [searchQuery, setSearchQuery]   = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter]     = useState('all');

  // Template types (fetched from API); categories are derived statically from type slug
  const [templateTypes, setTemplateTypes]           = useState([]);

  // Unified review modal state
  const [modal, setModal]       = useState({ open: false, submission: null });
  const [action, setAction]     = useState(null); // 'approved' | 'rejected' | null
  const [categoryId, setCategoryId]             = useState('');
  const [templateTypeId, setTemplateTypeId]     = useState('');
  const [templateCategory, setTemplateCategory] = useState(''); // subcategory string
  const [isPremium, setIsPremium]               = useState(false);
  const [feedback, setFeedback]                 = useState('');
  const [submitting, setSubmitting]             = useState(false);
  const [rejectDialog, setRejectDialog]         = useState({ open: false, submission: null, reason: '' });
  const [quickActionId, setQuickActionId]       = useState(null);
  const [adminPreviewPageIdx, setAdminPreviewPageIdx] = useState(0);

  // Delete state
  const [deleteDialog, setDeleteDialog]   = useState({ open: false, submission: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedIds, setSelectedIds]     = useState(new Set());
  const [bulkDeleting, setBulkDeleting]   = useState(false);

  // Subcategories derived from the currently selected type slug — no API call needed
  const modalCategories = getCategoriesForSlug(
    templateTypes.find(t => t.id === templateTypeId)?.slug || ''
  );

  /* ── Data fetching ──────────────────────────────────────────────── */
  const token = () => localStorage.getItem('admin_token');

  const fetchSubmissions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/content/submissions`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) {
        if (res.status === 401) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          window.location.href = '/admin/login';
          return;
        }
        const msg = res.status === 403 ? 'Admin access required'
          : `Failed to load submissions (${res.status})`;
        toast({ title: 'Error', description: msg, variant: 'destructive' });
        setSubmissions([]);
        return;
      }
      setSubmissions((await res.json()) || []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load submissions', variant: 'destructive' });
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
    // Fetch template types for the review modal dropdown
    fetch(`${API_URL}/api/public/template-types`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setTemplateTypes(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  /* ── Modal helpers ──────────────────────────────────────────────── */
  const openModal = (submission) => {
    setModal({ open: true, submission });
    setAction(null);
    setCategoryId('');
    const typeId = submission.template_type_id || '';
    setTemplateTypeId(typeId);
    setTemplateCategory(submission.category || '');
    setIsPremium(false);
    setFeedback('');
    setAdminPreviewPageIdx(0);
  };

  const closeModal = () => {
    setModal({ open: false, submission: null });
    setAction(null);
    setFeedback('');
  };

  /* ── Submit review ──────────────────────────────────────────────── */
  const submitReview = async (forcedAction) => {
    const act = forcedAction || action;
    if (!modal.submission || !act) return;
    if (act === 'rejected' && !feedback.trim()) {
      toast({ title: 'Feedback required', description: 'Please provide a reason for rejection.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/content/submissions/${modal.submission.id}/review`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
          body: JSON.stringify({
            status: act,
            rejection_reason: act === 'rejected' ? feedback : undefined,
            review_notes: feedback || undefined,
            category_id: categoryId || undefined,
            template_type: templateTypes.find(t => t.id === templateTypeId)?.name || undefined,
            template_type_id: templateTypeId || undefined,
            category: templateCategory || undefined,
            is_premium: isPremium,
          }),
        }
      );
      if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        window.location.href = '/admin/login';
        return;
      }
      const data = await res.json();
      if (!res.ok) {
        toast({ title: 'Failed', description: data.detail || 'Could not submit review', variant: 'destructive' });
        return;
      }
      toast({
        title: act === 'approved' ? '✓ Approved' : 'Rejected',
        description: act === 'approved'
          ? 'Template approved. Use the Publish button to make it public.'
          : 'Designer has been notified.',
      });
      closeModal();
      fetchSubmissions();
    } catch {
      toast({ title: 'Error', description: 'Could not submit review', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Quick actions (no modal) ───────────────────────────────────── */
  const handleQuickApprove = async (s) => {
    setQuickActionId(s.id);
    try {
      const res = await fetch(`${API_URL}/api/admin/content/submissions/${s.id}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ status: 'approved', is_premium: false }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
      toast({ title: 'Approved', description: `"${s.name || s.title || 'Template'}" approved. Click Publish to make it public.` });
      fetchSubmissions();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setQuickActionId(null);
    }
  };

  const handleQuickReject = (s) => {
    setRejectDialog({ open: true, submission: s, reason: '' });
  };

  const submitQuickReject = async () => {
    const s = rejectDialog.submission;
    if (!s || !rejectDialog.reason.trim()) return;
    setQuickActionId(s.id);
    try {
      const res = await fetch(`${API_URL}/api/admin/content/submissions/${s.id}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ status: 'rejected', rejection_reason: rejectDialog.reason, review_notes: rejectDialog.reason }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
      toast({ title: 'Rejected', description: 'Designer has been notified.' });
      setRejectDialog({ open: false, submission: null, reason: '' });
      fetchSubmissions();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setQuickActionId(null);
    }
  };


  /* ── Delete handlers ────────────────────────────────────────────── */
  const confirmDelete = (s) => setDeleteDialog({ open: true, submission: s });
  const cancelDelete  = () => setDeleteDialog({ open: false, submission: null });

  const handleDelete = async () => {
    const s = deleteDialog.submission;
    if (!s) return;
    setDeleteLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/content/submissions/${s.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast({ title: 'Error', description: data.detail || 'Could not delete submission', variant: 'destructive' });
        return;
      }
      // Optimistic UI — remove without reload
      setSubmissions(prev => prev.filter(x => x.id !== s.id));
      setSelectedIds(prev => { const n = new Set(prev); n.delete(s.id); return n; });
      toast({ title: 'Deleted', description: `"${s.name || s.title || 'Untitled'}" has been permanently deleted.` });
      cancelDelete();
    } catch {
      toast({ title: 'Error', description: 'Network error — could not delete', variant: 'destructive' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const res = await fetch(`${API_URL}/api/admin/content/submissions/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ ids }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast({ title: 'Error', description: data.detail || 'Bulk delete failed', variant: 'destructive' });
        return;
      }
      setSubmissions(prev => prev.filter(x => !selectedIds.has(x.id)));
      setSelectedIds(new Set());
      toast({ title: 'Deleted', description: `${data.deleted_count ?? ids.length} submission(s) permanently deleted.` });
    } catch {
      toast({ title: 'Error', description: 'Network error — bulk delete failed', variant: 'destructive' });
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSelect = (id) => {
    const scrollY = window.scrollY;
    setSelectedIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
    requestAnimationFrame(() => window.scrollTo(0, scrollY));
  };

  const toggleSelectAll = () => {
    const scrollY = window.scrollY;
    setSelectedIds(prev => {
      const allIds = filtered.map(s => s.id);
      const allSelected = allIds.every(id => prev.has(id));
      const next = allSelected ? new Set() : new Set(allIds);
      return next;
    });
    requestAnimationFrame(() => window.scrollTo(0, scrollY));
  };

  const handlePublish = async (s) => {
    setQuickActionId(s.id);
    try {
      const res = await fetch(`${API_URL}/api/admin/content/submissions/${s.id}/publish`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
      toast({ title: '✓ Published', description: 'Template is now live in the template library.' });
      fetchSubmissions();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setQuickActionId(null);
    }
  };

  /* ── Helpers ────────────────────────────────────────────────────── */
  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return iso; }
  };

  const filtered = submissions.filter(s => {
    const q = searchQuery.toLowerCase();
    const matchSearch = !q || (s.name || '').toLowerCase().includes(q) || (s.submitted_by_name || '').toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || (s.status || 'pending') === statusFilter;
    const matchType   = typeFilter   === 'all' || s.type === typeFilter;
    return matchSearch && matchStatus && matchType;
  });

  const counts = {
    all:       submissions.length,
    pending:   submissions.filter(s => !s.status || s.status === 'pending').length,
    approved:  submissions.filter(s => s.status === 'approved').length,
    rejected:  submissions.filter(s => s.status === 'rejected').length,
    published: submissions.filter(s => s.status === 'published').length,
  };

  const StatusTab = ({ value, label }) => (
    <button
      onClick={() => setStatusFilter(value)}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
        statusFilter === value ? 'bg-violet-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      {label}
      <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-md tabular-nums ${
        statusFilter === value ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
      }`}>{counts[value]}</span>
    </button>
  );

  /* ── Submission row ─────────────────────────────────────────────── */
  const SubmissionRow = ({ s }) => {
    const status = s.status || 'pending';
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const isPending = status === 'pending';
    const canvasW = s.canvas_width || s.canvas_size?.width || 1080;
    const canvasH = s.canvas_height || s.canvas_size?.height || 1080;
    // designer_templates store pages inside template_json; legacy designs store at s.pages
    const firstPage = s.template_json?.pages?.[0] ?? s.pages?.[0] ?? null;

    // Detect effects that html2canvas captures unreliably (blur, clip masks).
    // When present, skip the stored JPEG and use the live CSS renderer instead.
    const hasAdvancedEffects = !!firstPage && (
      (firstPage.backgroundConfig?.blur || 0) > 0 ||
      (firstPage.elements || []).some(el => el.clippedTo || el.isMaskShape || el.isClipBase)
    );

    // Priority chain:
    //  • Design has blur/clip  → PageThumbnailPreview (always correct) → initial tile
    //  • Simple design          → stored thumbnail → firstPageBg → PageThumbnailPreview → initial tile
    const firstPageBg = (() => {
      const cfg = firstPage?.backgroundConfig;
      return cfg?.type === 'image' && cfg?.url ? cfg.url : null;
    })();
    const rowCandidates = hasAdvancedEffects
      ? [] // go straight to PageThumbnailPreview
      : [s.thumbnail, s.preview_url, firstPageBg].filter(Boolean);
    const [rowSrcIdx, setRowSrcIdx] = React.useState(0);
    React.useEffect(() => { setRowSrcIdx(0); }, [s.id]);
    const rowAllFailed = rowSrcIdx >= rowCandidates.length;
    const rowSrc = rowAllFailed ? null : rowCandidates[rowSrcIdx];

    // Determine whether PageThumbnailPreview will render visually meaningful content.
    // A design is "visually blank" when it has only text elements on a near-white background
    // (at 56×56 thumbnail scale, 12–14px text renders as sub-pixel lines that are invisible).
    const _LIGHT_BG = new Set(['#fff', '#ffffff', 'white', '#f3f4f6', '#f9fafb', '']);
    const pageHasVisualContent = !!firstPage && (
      (_LIGHT_BG.has((firstPage.background || '').toLowerCase())
        ? false  // re-evaluated below
        : true) ||
      firstPage.backgroundConfig?.type === 'image' ||
      firstPage.backgroundConfig?.type === 'gradient' ||
      (firstPage.elements || []).some(el => el.type === 'image' || el.type === 'svg' ||
        (el.type === 'shape' && el.fill && !_LIGHT_BG.has((el.fill || '').toLowerCase())))
    );

    // Colorful initial tile — shown when no stored image AND no visual canvas content.
    // Uses the shared LetterTile (same tile in designer/admin/AI pages).
    const designLabel = (s.name || s.title || 'U').trim();

    const isSelected = selectedIds.has(s.id);

    return (
      <div className={`bg-white border rounded-2xl p-4 flex items-center gap-4 hover:shadow-sm transition-all ${
        isSelected ? 'border-red-300 bg-red-50/30' : isPending ? 'border-amber-200' : 'border-gray-100'
      }`}>
        {/* Bulk-select checkbox — all statuses */}
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => toggleSelect(s.id)}
          onClick={e => e.stopPropagation()}
          className="w-4 h-4 rounded border-gray-300 text-red-500 accent-red-500 flex-shrink-0 cursor-pointer"
        />

        {/* Thumbnail: live CSS render for blur/clip designs; stored JPEG otherwise */}
        <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 flex-shrink-0 relative">
          {/* Initial tile shown behind — visible when image/preview fails or page is blank */}
          <LetterTile label={designLabel} textClassName="text-white text-xl font-bold select-none" />

          {rowSrc && (
            <img
              key={rowSrc}
              src={rowSrc}
              alt=""
              onError={() => setRowSrcIdx(i => i + 1)}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          {rowAllFailed && firstPage && pageHasVisualContent && (
            <PageThumbnailPreview
              page={firstPage}
              canvasSize={{ width: canvasW, height: canvasH }}
              fillContainer={true}
            />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-gray-900 text-sm truncate">{s.name || s.title || 'Untitled'}</p>
            {s.type && (
              <span className="text-[11px] px-2 py-0.5 rounded-md border border-gray-200 text-gray-500 capitalize">{s.type}</span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400 flex-wrap">
            <span className="flex items-center gap-1"><User className="w-3 h-3" />{s.submitted_by_name || s.designer_name || 'Unknown'}</span>
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmt(s.submitted_at)}</span>
            {(s.canvas_width || s.canvas_size?.width) && (
              <span className="flex items-center gap-1">
                <Ruler className="w-3 h-3" />
                {s.canvas_width || s.canvas_size?.width}×{s.canvas_height || s.canvas_size?.height}px
              </span>
            )}
            {s.tags?.length > 0 && (
              <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{s.tags.slice(0, 3).join(', ')}</span>
            )}
          </div>
          {s.review_notes && (
            <p className="mt-1 text-xs text-red-500 truncate">
              <span className="font-medium">Feedback:</span> {s.review_notes}
            </p>
          )}
        </div>

        {/* Status */}
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium flex-shrink-0 ${cfg.bg}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
          {cfg.label}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => openModal(s)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 transition-colors"
          >
            <Eye className="w-3.5 h-3.5" />
            Review
          </button>
          {isPending && (
            <>
              <button
                onClick={() => handleQuickApprove(s)}
                disabled={quickActionId === s.id}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                {quickActionId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                Approve
              </button>
              <button
                onClick={() => handleQuickReject(s)}
                disabled={quickActionId === s.id}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                <XCircle className="w-3.5 h-3.5" />
                Reject
              </button>
            </>
          )}
          {status === 'approved' && (
            <button
              onClick={() => handlePublish(s)}
              disabled={quickActionId === s.id}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              {quickActionId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
              Publish
            </button>
          )}
          <button
            onClick={() => confirmDelete(s)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </button>
        </div>
      </div>
    );
  };

  /* ── Unified Review Modal ───────────────────────────────────────── */
  const ReviewModal = () => {
    const s = modal.submission;
    if (!s) return null;
    const isPending = !s.status || s.status === 'pending';

    const canvasW = s.canvas_width || s.canvas_size?.width || 1080;
    const canvasH = s.canvas_height || s.canvas_size?.height || 1080;
    const canvasSize = { width: canvasW, height: canvasH };
    // Pages from template_json (new flow) or top-level pages (legacy designs)
    const rawPages = (s.template_json?.pages?.length > 0 ? s.template_json.pages : null) ?? (s.pages?.length > 0 ? s.pages : null);
    const templatePages = Array.isArray(rawPages) && rawPages.length > 0 ? rawPages : null;
    const status = s.status || 'pending';
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden">

          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                <FileText className="w-4 h-4 text-violet-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">{s.name || s.title || 'Untitled'}</p>
                <p className="text-xs text-gray-400">Submitted by {s.submitted_by_name || s.designer_name || 'Unknown'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                {cfg.label}
              </div>
              <button onClick={closeModal} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors text-lg font-light">
                ×
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="flex-1 overflow-hidden flex flex-col lg:flex-row min-h-0">

            {/* LEFT — Design Preview (live-rendered from template_json) */}
            <div className="lg:w-[58%] bg-gray-50 border-r border-gray-100 flex flex-col overflow-hidden">
              {/* Panel header */}
              <div className="px-5 py-3 border-b border-gray-100 bg-white flex-shrink-0 flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Design Preview</p>
                {templatePages && templatePages.length > 1 && (
                  <span className="text-[11px] text-gray-400">{templatePages.length} pages</span>
                )}
              </div>

              {/* Main preview area */}
              <div className="flex-1 overflow-auto flex items-center justify-center p-6">
                {templatePages ? (
                  <div
                    className="rounded-xl overflow-hidden border border-gray-200"
                    style={{
                      boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
                      width: '100%',
                      maxWidth: `min(500px, ${Math.round(500 * canvasW / canvasH)}px)`,
                    }}
                  >
                    <PageThumbnailPreview
                      page={templatePages[adminPreviewPageIdx] || templatePages[0]}
                      canvasSize={canvasSize}
                      fillWidth={true}
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center text-center p-12">
                    <div className="w-20 h-20 rounded-2xl bg-gray-100 border-2 border-dashed border-gray-200 flex items-center justify-center mb-4">
                      <ImageIcon className="w-8 h-8 text-gray-300" />
                    </div>
                    <p className="text-sm font-medium text-gray-400">No preview available</p>
                    <p className="text-xs text-gray-300 mt-1">Template data not found</p>
                  </div>
                )}
              </div>

              {/* Page filmstrip — only when multiple pages */}
              {templatePages && templatePages.length > 1 && (
                <div className="flex-shrink-0 border-t border-gray-100 bg-white px-4 py-3">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2">Pages</p>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {templatePages.map((page, i) => {
                      const isActive = adminPreviewPageIdx === i;
                      return (
                        <button
                          key={page.id || i}
                          onClick={() => setAdminPreviewPageIdx(i)}
                          className={`flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all w-14 h-14 group relative ${
                            isActive
                              ? 'border-violet-500 ring-2 ring-violet-200'
                              : 'border-gray-200 hover:border-violet-300'
                          }`}
                          title={`Page ${i + 1}`}
                        >
                          <PageThumbnailPreview
                            page={page}
                            canvasSize={canvasSize}
                            fillContainer={true}
                          />
                          <div className={`absolute bottom-0 inset-x-0 py-0.5 text-center text-[9px] font-bold transition-colors ${
                            isActive ? 'bg-violet-500 text-white' : 'bg-black/30 text-white group-hover:bg-black/50'
                          }`}>
                            {i + 1}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT — Details + Actions */}
            <div className="lg:w-[42%] flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">

                {/* Template Details */}
                <div className="px-5 py-4 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Template Details</p>
                  <div className="space-y-2.5">
                    <Detail icon={FileText} label="Title" value={s.name || s.title || 'Untitled'} />
                    <Detail icon={User}     label="Designer" value={s.submitted_by_name || s.designer_name || '—'} />
                    <Detail icon={Hash}     label="Type" value={s.type || '—'} capitalize />
                    <Detail icon={Clock}    label="Submitted" value={fmt(s.submitted_at)} />
                    {(canvasW || canvasH) && (
                      <Detail icon={Ruler} label="Dimensions" value={`${canvasW}×${canvasH}px`} mono />
                    )}
                    {s.template_type && (
                      <Detail icon={Grid3X3} label="Template Type" value={
                        templateTypes.find(t => t.id === s.template_type_id)?.name || s.template_type
                      } />
                    )}
                    {s.tags?.length > 0 && (
                      <div className="flex items-start gap-2.5">
                        <Tag className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-[11px] text-gray-400">Tags</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {s.tags.map(t => (
                              <span key={t} className="text-[11px] px-2 py-0.5 bg-gray-100 rounded-md text-gray-600">{t}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    {s.description && (
                      <div className="mt-1 p-3 bg-gray-50 rounded-xl">
                        <p className="text-[11px] text-gray-400 mb-1">Description</p>
                        <p className="text-xs text-gray-600 leading-relaxed">{s.description}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Previous feedback (if rejected) */}
                {s.review_notes && (
                  <div className="px-5 py-4 border-b border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">Previous Feedback</p>
                    <div className="p-3 bg-red-50 border border-red-100 rounded-xl">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                        <p className="text-xs font-semibold text-red-700">Rejection Reason</p>
                      </div>
                      <p className="text-xs text-red-600 leading-relaxed">{s.review_notes}</p>
                    </div>
                  </div>
                )}

                {/* Admin Actions (only for pending) */}
                {isPending && (
                  <div className="px-5 py-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Review Actions</p>
                    <div className="space-y-3">

                      {/* Template Type */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Template Type</label>
                        <div className="relative">
                          <select
                            value={templateTypeId}
                            onChange={e => {
                              setTemplateTypeId(e.target.value);
                              setTemplateCategory('');
                            }}
                            className="w-full appearance-none px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400"
                          >
                            <option value="">Select type…</option>
                            {templateTypes.map(t => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                      </div>

                      {/* Category — dynamic subcategories based on selected type */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Category</label>
                        <div className="relative">
                          <select
                            value={templateCategory}
                            onChange={e => setTemplateCategory(e.target.value)}
                            disabled={!templateTypeId}
                            className="w-full appearance-none px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 disabled:opacity-50"
                          >
                            <option value="">{templateTypeId ? 'No category (optional)' : 'Select a type first'}</option>
                            {modalCategories.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                      </div>

                      {/* Visibility */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Visibility</label>
                        <div className="flex gap-2">
                          {[
                            { value: false, label: 'Free',    icon: '🌐' },
                            { value: true,  label: 'Premium', icon: '⭐' },
                          ].map(opt => (
                            <button
                              key={String(opt.value)}
                              onClick={() => setIsPremium(opt.value)}
                              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${
                                isPremium === opt.value
                                  ? 'border-violet-400 bg-violet-50 text-violet-700'
                                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              <span>{opt.icon}</span>{opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Feedback / Rejection reason */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                          Feedback{action === 'rejected' ? <span className="text-red-500 ml-1">*required</span> : <span className="text-gray-400 ml-1">(optional)</span>}
                        </label>
                        <textarea
                          rows={3}
                          value={feedback}
                          onChange={e => setFeedback(e.target.value)}
                          placeholder={action === 'rejected'
                            ? 'e.g. Font readability issue on mobile devices. Please increase contrast.'
                            : 'Optional note for the designer…'}
                          className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 resize-none placeholder:text-gray-300"
                        />
                      </div>

                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0 flex items-center justify-between gap-3 bg-gray-50/50">
                <button onClick={closeModal} className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-white transition-colors">
                  Close
                </button>
                {isPending && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => submitReview('rejected')}
                      disabled={submitting || !feedback.trim()}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-red-500 hover:bg-red-600 disabled:opacity-40 text-white rounded-lg transition-colors"
                    >
                      {submitting && action === 'rejected' ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                      Reject
                    </button>
                    <button
                      onClick={() => submitReview('approved')}
                      disabled={submitting}
                      className="flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-lg transition-colors"
                    >
                      {submitting && action !== 'rejected' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      Approve
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ── Small helper component ─────────────────────────────────────── */
  const Detail = ({ icon: Icon, label, value, capitalize, mono }) => (
    <div className="flex items-start gap-2.5">
      <Icon className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-[11px] text-gray-400">{label}</p>
        <p className={`text-sm font-medium text-gray-800 truncate ${capitalize ? 'capitalize' : ''} ${mono ? 'font-mono text-xs' : ''}`}>
          {value}
        </p>
      </div>
    </div>
  );

  /* ── Render ─────────────────────────────────────────────────────── */
  return (
    <AdminLayout>
      <div className="space-y-6 w-full">

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Submissions</h1>
            <p className="text-sm text-gray-400 mt-0.5">Review and moderate designer template submissions.</p>
          </div>
          {counts.pending > 0 && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl px-4 py-2.5 flex-shrink-0">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-sm font-medium">{counts.pending} awaiting review</span>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl flex-wrap">
            <StatusTab value="all" label="All" />
            <StatusTab value="pending" label="Pending" />
            <StatusTab value="approved" label="Approved" />
            <StatusTab value="rejected" label="Rejected" />
            <StatusTab value="published" label="Published" />
          </div>
          <div className="flex items-center gap-2 flex-1 sm:justify-end flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                placeholder="Search by name or designer..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 w-56"
              />
            </div>
            <div className="relative">
              <select
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
                className="appearance-none pl-8 pr-8 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 w-40"
              >
                <option value="all">All Types</option>
                <option value="template">Templates</option>
                <option value="element">Elements</option>
                <option value="graphic">Graphics</option>
              </select>
              <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Bulk-delete toolbar — appears when rejected items are selected */}
        {selectedIds.size > 0 && (
          <div className="flex items-center justify-between gap-4 bg-red-50 border border-red-200 rounded-2xl px-5 py-3">
            <div className="flex items-center gap-2 text-sm text-red-700 font-medium">
              <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">
                {selectedIds.size}
              </span>
              {selectedIds.size === 1 ? '1 submission selected' : `${selectedIds.size} submissions selected`}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
              >
                Clear selection
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                Delete {selectedIds.size} selected
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-2xl flex flex-col items-center justify-center py-16">
            <Inbox className="w-14 h-14 text-gray-200 mb-4" />
            <p className="font-medium text-gray-700">
              {submissions.length === 0 ? 'No submissions yet' : 'No results match your filters'}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {submissions.length === 0
                ? 'When designers submit templates, they will appear here.'
                : 'Try adjusting your search or filter.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Select All row */}
            <div className="flex items-center gap-3 px-4 py-2">
              <input
                type="checkbox"
                checked={filtered.length > 0 && filtered.every(s => selectedIds.has(s.id))}
                ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && !filtered.every(s => selectedIds.has(s.id)); }}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-gray-300 accent-red-500 cursor-pointer"
              />
              <span className="text-xs text-gray-400 select-none">Select all ({filtered.length})</span>
            </div>
            {filtered.map(s => <SubmissionRow key={s.id} s={s} />)}
          </div>
        )}
      </div>

      {/* Unified Review Modal */}
      {modal.open && <ReviewModal />}

      {/* Delete Confirmation Modal */}
      {deleteDialog.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="px-6 pt-6 pb-4 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <p className="font-semibold text-gray-900 text-base">Delete Design?</p>
              <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">
                Are you sure you want to permanently delete{' '}
                <span className="font-medium text-gray-700">
                  "{deleteDialog.submission?.name || deleteDialog.submission?.title || 'Untitled'}"
                </span>?
                <br />This action cannot be undone.
              </p>
            </div>
            <div className="px-6 pb-6 flex items-center justify-center gap-3">
              <button
                onClick={cancelDelete}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteLoading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl transition-colors"
              >
                {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Reject Dialog */}
      {rejectDialog.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <p className="font-semibold text-gray-900">Reject Submission</p>
              <p className="text-xs text-gray-400 mt-0.5">
                "{rejectDialog.submission?.name || rejectDialog.submission?.title || 'Untitled'}"
              </p>
            </div>
            <div className="px-6 py-4">
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Rejection reason <span className="text-red-500">*required</span>
              </label>
              <textarea
                rows={3}
                value={rejectDialog.reason}
                onChange={e => setRejectDialog(d => ({ ...d, reason: e.target.value }))}
                placeholder="e.g. Low image quality, incorrect dimensions, off-brand colors…"
                className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 resize-none placeholder:text-gray-300"
                autoFocus
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                onClick={() => setRejectDialog({ open: false, submission: null, reason: '' })}
                className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submitQuickReject}
                disabled={!rejectDialog.reason.trim() || quickActionId !== null}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-red-500 hover:bg-red-600 disabled:opacity-40 text-white rounded-lg transition-colors"
              >
                {quickActionId ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSubmissions;
